import { motion } from "framer-motion";
import { Award, Users, Globe, Shield, Clock, Star, Trophy, Target, Heart } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const AboutSection = () => {
  const { t } = useLanguage();

  const stats = [
    { number: "10+", labelKey: "about.stat1", icon: Trophy },
    { number: "50K+", labelKey: "about.stat2", icon: Users },
    { number: "4.8", labelKey: "about.stat3", icon: Star },
    { number: "7/24", labelKey: "about.stat4", icon: Clock }
  ];

  const features = [
    { icon: Shield, labelKey: "about.feature1" },
    { icon: Globe, labelKey: "about.feature2" },
    { icon: Award, labelKey: "about.feature3" },
    { icon: Target, labelKey: "about.feature4" }
  ];

  return (
    <section id="about" className="py-24 bg-[#050505] relative overflow-hidden" data-testid="about-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-mono tracking-[0.3em] text-[#00FFFF] uppercase mb-4 block">
            {t("about.subtitle")}
          </span>
          <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">{t("about.title1")}</span>{" "}
            <span className="text-[#00FFFF]">{t("about.title2")}</span>
          </h2>
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Left: Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1606030612733-c777c842d224?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxwcml2YXRlJTIwcGFydHklMjBkYW5jZXIlMjBzaWxob3VldHRlfGVufDB8fHx8MTc3OTQwNzkxMnww&ixlib=rb-4.1.0&q=85&w=800"
                alt="First Lady Entertainment - Professional Service"
                className="w-full h-auto"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -bottom-6 -right-6 bg-[#D4AF37] p-6 shadow-2xl">
              <div className="text-center">
                <div className="font-['Bebas_Neue'] text-5xl text-black">2014</div>
                <div className="text-black text-sm font-bold uppercase tracking-wider">{t("about.established")}</div>
              </div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="font-['Syne'] text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
              <Heart className="text-[#FF00FF]" />
              {t("about.mainTitle")}
            </h3>
            
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>{t("about.para1")}</p>
              <p>{t("about.para2")}</p>
              <p>{t("about.para3")}</p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <feature.icon size={20} className="text-[#D4AF37]" />
                  <span className="text-white text-sm">{t(feature.labelKey)}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white/5 border border-white/10 p-6 text-center hover:border-[#D4AF37]/50 transition-colors"
              data-testid={`about-stat-${index}`}
            >
              <stat.icon size={28} className="mx-auto mb-3 text-[#D4AF37]" />
              <div className="font-['Bebas_Neue'] text-4xl md:text-5xl text-white mb-1">{stat.number}</div>
              <div className="text-gray-400 text-sm uppercase tracking-wider">{t(stat.labelKey)}</div>
            </div>
          ))}
        </motion.div>

        {/* SEO Bottom Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-500 text-sm max-w-4xl mx-auto leading-relaxed">
            {t("about.seoText")}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
